import { useState } from "react";

import { formatInteger } from "@/lib/format";
import { useAccount } from "@/lib/useAccount";
import type { Puzzle, RatingDraft } from "@/lib/puzzlesApi";
import { PuzzleRatingForm } from "./PuzzleRatingForm";
import { Stat } from "./Stat";

interface PuzzleCardProps {
  readonly puzzle: Puzzle;
  readonly onRate: (id: number, draft: RatingDraft) => Promise<Puzzle>;
  readonly onUnrate: (id: number) => Promise<void>;
}

export function PuzzleCard({ puzzle, onRate, onUnrate }: PuzzleCardProps) {
  const { session } = useAccount();
  const [open, setOpen] = useState(false);

  const community = puzzle.ratings;

  // Both difficulties are shown, never one instead of the other. They mean
  // genuinely different things — one person's guess when they added the puzzle
  // versus what everyone who tried it thought — and the gap between them is the
  // most interesting number on the card.
  const creatorDifficulty = formatInteger(puzzle.archive_difficulty);
  // Not formatInteger: this one is an average, and rounding 6.2 to 6 would hide
  // exactly the disagreement the two columns exist to show.
  const clubDifficulty = community.difficulty !== null ? String(community.difficulty) : "—";
  const clubNote =
    community.count > 0 ? `from ${community.count}` : "no ratings yet";

  return (
    <article className="panel px-5 py-4">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="meta font-mono opacity-50">
          #{String(puzzle.id).padStart(3, "0")}
        </span>
        <h3 className="text-xl leading-tight normal-case">{puzzle.title}</h3>
        {puzzle.set_name ? (
          <span className="meta border-2 border-ink bg-piece-o px-1.5 py-0.5 text-[0.625rem]">
            {puzzle.set_name}
          </span>
        ) : null}
        {puzzle.creator ? (
          <span className="meta ml-auto opacity-60">by {puzzle.creator}</span>
        ) : null}
      </div>

      <div className="mt-4 flex flex-wrap items-end gap-x-8 gap-y-3">
        <Stat label="Creator difficulty" value={creatorDifficulty} />
        <Stat label="Club difficulty" value={clubDifficulty} note={clubNote} />
        <Stat
          label="Quality"
          value={community.quality !== null ? `${community.quality}/5` : "—"}
        />
        <Stat
          label="Solved"
          value={
            community.solved_rate !== null ? `${Math.round(community.solved_rate * 100)}%` : "—"
          }
        />
        {community.character ? <Stat label="Kind" value={community.character} /> : null}
      </div>

      <div className="mt-5 flex flex-wrap gap-2.5">
        <a
          className="btn px-3 py-1.5 text-xs"
          href={puzzle.puzzle_url}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open puzzle
        </a>
        <a
          className="btn btn--ghost px-3 py-1.5 text-xs"
          href={puzzle.solution_url}
          target="_blank"
          rel="noopener noreferrer"
        >
          Solution
        </a>
        {session ? (
          <button
            type="button"
            className="btn btn--ghost px-3 py-1.5 text-xs"
            aria-expanded={open}
            onClick={() => setOpen(!open)}
          >
            {puzzle.my_rating ? `Your rating: ${puzzle.my_rating.difficulty}` : "Rate it"}
          </button>
        ) : null}
      </div>

      {session && open ? (
        <PuzzleRatingForm
          puzzle={puzzle}
          onSubmit={(draft) => onRate(puzzle.id, draft)}
          onRemove={() => onUnrate(puzzle.id)}
        />
      ) : null}
    </article>
  );
}
